import React from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Select,
} from '@chakra-ui/react';
import { SearchFilter } from '../../../components/SearchFilter';
import { DateRange, DynamicDateRange } from '../../../components/DateRangeSelector/DynamicDateRange';
import { PAYMENT_LINK_STATUSES } from '../models/payment-link-view';

export interface PaymentLinksTableFilters {
  concept?: string;
  status?: string;
  amountMin?: string;
  amountMax?: string;
  dateStart?: string;
  dateEnd?: string;
}

interface PaymentLinksFiltersProps {
  filters: PaymentLinksTableFilters;
  onConceptChange: (value?: string) => void;
  onStatusChange: (value?: string) => void;
  onAmountMinChange: (value?: string) => void;
  onAmountMaxChange: (value?: string) => void;
  onDateRangeChange: (range: { start?: string; end?: string }) => void;
}

// No usa date-fns/format: ese subpath es ESM-only y rompe bajo ts-jest (no está en
// transformIgnorePatterns), a diferencia de date-fns/locale que sí está mockeado.
export const toIsoDateString = (date: Date): string => {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const defaultPaymentLinksFilters = (): PaymentLinksTableFilters => {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  return { dateStart: toIsoDateString(yesterday), dateEnd: toIsoDateString(today) };
};

/**
 * Panel de filtros de la Consulta de Links de Pago, fuera de la tabla, con el mismo
 * comportamiento que `TransactionFilters` (acordeón "Filtros" colapsable). Los filtros se
 * resuelven en el BACKEND (bff/payment-links-view/broxel/raw → microservicio): cada cambio
 * dispara una nueva consulta y reinicia la paginación al conjunto filtrado.
 */
export const PaymentLinksFilters = (props: PaymentLinksFiltersProps) => {
  const { filters, onConceptChange, onStatusChange, onAmountMinChange, onAmountMaxChange, onDateRangeChange } = props;

  const handleDateRangeChange = (range: DateRange) => {
    onDateRangeChange({
      start: range.startDate ? toIsoDateString(range.startDate) : undefined,
      end: range.endDate ? toIsoDateString(range.endDate) : undefined,
    });
  };

  return (
    <Accordion allowToggle marginBottom="xl" padding="l" borderWidth="1px" borderColor="neutral.200" borderRadius="xl" overflow="show">
      <AccordionItem border="none">
        <AccordionButton>
          <Flex width="full" justifyContent="space-between">
            <Box>Filtros</Box>
            <Box><AccordionIcon /></Box>
          </Flex>
        </AccordionButton>
        <AccordionPanel>
          <Flex alignItems="center" wrap="wrap" gap="l" zIndex={200}>
            <SearchFilter
              value={filters.concept}
              handleChange={onConceptChange}
              placeholder="Buscar concepto..."
              width="16em"
              debounceDelay={300}
            />

            <SearchFilter
              value={filters.amountMin}
              handleChange={onAmountMinChange}
              placeholder="Monto mínimo"
              inputType="number"
              width="10em"
              debounceDelay={300}
            />

            <SearchFilter
              value={filters.amountMax}
              handleChange={onAmountMaxChange}
              placeholder="Monto máximo"
              inputType="number"
              width="10em"
              debounceDelay={300}
            />

            <Select
              value={filters.status || 'all'}
              onChange={(e) => onStatusChange(e.target.value === 'all' ? undefined : e.target.value)}
              width="12em"
            >
              <option value="all">Estado: Todos</option>
              {PAYMENT_LINK_STATUSES.map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </Select>

            <DynamicDateRange
              onDateRangeChange={handleDateRangeChange}
              startDate={filters.dateStart}
              endDate={filters.dateEnd}
              useDefaultDates={false}
            />
          </Flex>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};
